<template>
  <LoaderWrapper ref="loader" class="d-flex float-start">
  <div class="d-flex flex-row">
    <div v-if="showControls && (isAdmin || isOperator) && trader !== null" class="d-flex align-items-center me-2">
      <i v-b-tooltip.hover :title="helper.trans('labels.user_blocked')" v-if="trader.isBlocked" class="fa fa-ban text-danger text-center" style="width: 2.5rem"></i>
      <div style="width: 2.5rem" class="d-flex flex-column" v-else>
        <small class="form-switch" v-b-tooltip.hover.left :title="`${helper.trans('direction.in')}: ${helper.trans(trader.isActive ? 'trader.status.active' : 'trader.status.inactive')}`">
          <input id="traderStatus" @click.prevent="toggleTraderStatus(trader.id, !trader.isActive, Direction.PAY_IN)" class="form-check-input" style="cursor: pointer" type="checkbox" role="switch" :checked="trader.isActive">
        </small>
        <small class="form-switch" v-b-tooltip.hover.left :title="`${helper.trans('direction.out')}: ${helper.trans(trader.isPayOutActive ? 'trader.status.active' : 'trader.status.inactive')}`">
          <input id="traderStatus" @click.prevent="toggleTraderStatus(trader.id, !trader.isPayOutActive, Direction.PAY_OUT)" class="form-check-input" style="cursor: pointer" type="checkbox" role="switch" :checked="trader.isPayOutActive">
        </small>
      </div>
      <div>
        <button :disabled="trader.isBlocked" v-if="!trader.isPayInTrafficBlocked" v-b-tooltip.hover :title="helper.trans('trader.status.active')" @click.prevent="toggleTraderTraffic(trader, Direction.PAY_IN, false)" class="btn-sm btn btn-outline-success"><i class="fa fa-circle-play"></i></button>
        <button :disabled="trader.isBlocked" v-else v-b-tooltip.hover :title="helper.trans('labels.traffic_blocked')" @click.prevent="toggleTraderTraffic(trader, Direction.PAY_IN, true)" class="btn btn-sm btn-outline-warning"><i class="fa fa-circle-pause"></i></button>

        <button :disabled="trader.isBlocked" v-if="!trader.isPayOutTrafficBlocked" v-b-tooltip.hover :title="helper.trans('trader.status.active')" @click.prevent="toggleTraderTraffic(trader, Direction.PAY_OUT, false)" class="ms-2 btn btn-sm btn-outline-success"><i class="fa fa-circle-play"></i></button>
        <button :disabled="trader.isBlocked" v-else v-b-tooltip.hover :title="helper.trans('labels.traffic_blocked')" @click.prevent="toggleTraderTraffic(trader, Direction.PAY_OUT, true)" class="ms-2 btn btn-sm btn-outline-warning"><i class="fa fa-circle-pause"></i></button>
      </div>
    </div>
    <div class="d-flex flex-column" v-if="user">
      <div>
        <small v-if="showBadge" class="badge me-1" v-b-tooltip.hover :title="helper.trans(`user_roles.${user.role}`)"
               :class="getBadgeClass(user.role)"
        >
          {{helper.trans(`user_roles.shorts.${user.role}`)}}
        </small>
        <span>{{ user.name }}</span>
      </div>
      <small>{{ user.email }}</small>
    </div>
  </div>
  </LoaderWrapper>
</template>

<script lang="ts">

import {computed, defineComponent, Prop, PropType, ref} from 'vue'
import {helper} from "@/service/CommonHelper";
import UserDTO from "@/models/UserDTO";
import {UserRole} from "@/models/enum/UserRole";
import APIClient from "@/service/APIClient";
import TraderDTO from "@/models/TraderDTO";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {useStore} from "vuex";
import {Direction} from "@/models/enum/Direction";

const api = new APIClient()
export default defineComponent({
  name: "UserNameLabel",
  emits: ['trader:updated'],
  components: {
    LoaderWrapper
  },
  props: {
    user: {
      type: Object as PropType<UserDTO>,
      default: null
    } as Prop<UserDTO>,
    showBadge: {
      type: Boolean as PropType<boolean>,
      default: false
    } as Prop<boolean>,
    showControls: {
      type: Boolean as PropType<boolean>,
      default: false
    } as Prop<boolean>,
  },
  setup(props, {emit}) {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const store = useStore();

    const getBadgeClass = (role: string) => {
      switch (role) {
        case UserRole.TRADER: return {'bg-primary': true}
        case UserRole.MERCHANT: return {'bg-secondary': true}
        case UserRole.MERCHANT_OPERATOR: return {'bg-secondary': true}
        case UserRole.TRADER_TEAM_LEAD:
        case UserRole.REFERRAL: return {'bg-info': true}
        case UserRole.OPERATOR:  return {'bg-warning': true}
        case UserRole.ADMIN:  return {'bg-dark': true}
        case 'SYSTEM':  return {'bg-dark': true}
      }
    }

    const trader = computed<TraderDTO|null>(() => {
      const trader = props.user as unknown as TraderDTO
      if (trader.isPayInTrafficBlocked === null) {
        return null
      }
      return trader
    })

    const toggleTraderStatus = async (id: number, status: boolean, direction: Direction) => {
      loader.value?.show()
      try {
        const traderDTO = await api.changeTraderStatus(id, status, direction)
        emit('trader:updated', traderDTO)
      } finally {
        loader.value?.hide()
      }
    }

    const toggleTraderTraffic = async (trader: TraderDTO, direction: Direction, status: boolean) => {
      loader.value?.show()
      try {
        const traderDTO = await api.tradersToggleTraffic(trader.id, direction, status ? 'enable' : 'disable')
        emit('trader:updated', traderDTO)
      } finally {
        loader.value?.hide()
      }
    }

    return {
      helper,
      UserRole,
      getBadgeClass,
      toggleTraderStatus,
      toggleTraderTraffic,
      Direction,
      trader,
      isOperator: computed(() => store.getters.isOperator),
      isAdmin: computed(() => store.getters.isAdmin),
      loader
    }
  }
})
</script>
